// modules/freshcards/src/core/overlays/facePreference.ts
//
// 2026-09-18 (PROMPT F 19 Phase 2a) — FacePreference = per-scope overlay
// preference record. Keyed by cascading scope (surface > project > account).
//
// Storage: uses existing KV storage singleton (IndexedDBAdapter keyPath='key').
// No new IDB store needed — keys are namespaced as 'facepref:<scope>'.

import type { AppOverlay } from '../../types/models/AppOverlay';

/** Cascading scope: surface ⊃ project ⊃ account. Undefined = wildcard. */
export interface FaceScope {
  userId?: string;
  projectId?: string;
  surfaceId?: string;
}

/** FacePreference = the user's overlay choice at a scope. */
export interface FacePreference {
  /** Stable identifier (matches AppOverlay.id). */
  overlayId: string;
  /** Optional overrides applied on top of the overlay. */
  overrides?: Partial<AppOverlay>;
  /** When this preference was first set (ISO). */
  setAt: string;
  /** When this preference was last updated (ISO). */
  updatedAt: string;
}

/** Storage key formatter: 'facepref:user|project|surface' (undefined → '~'). */
export function scopeKey(scope: FaceScope): string {
  return `facepref:${scope.userId ?? '~'}|${scope.projectId ?? '~'}|${scope.surfaceId ?? '~'}`;
}

/** Sentinel key used by the migration flag. */
export const MIGRATION_FLAG_KEY = 'facepref:migrated-from-localstorage-v1';

/** Storage adapter shape we depend on (loose — avoid circular imports). */
export interface FacePreferenceStorage {
  load(key: string): Promise<unknown>;
  save(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
}

/** Load preference at scope (returns null if not set). */
export async function loadFacePreference(
  storage: FacePreferenceStorage,
  scope: FaceScope,
): Promise<FacePreference | null> {
  try {
    const raw = await storage.load(scopeKey(scope));
    if (!raw) return null;
    if (typeof raw !== 'object' || raw === null) return null;
    const r = raw as Record<string, unknown>;
    if (typeof r.overlayId !== 'string') return null;
    return r as unknown as FacePreference;
  } catch (e) {
    console.error('[facePreference] load failed', scopeKey(scope), e);
    return null;
  }
}

/** Save preference at scope. */
export async function saveFacePreference(
  storage: FacePreferenceStorage,
  scope: FaceScope,
  pref: FacePreference,
): Promise<void> {
  const stamped: FacePreference = {
    ...pref,
    updatedAt: new Date().toISOString(),
    setAt: pref.setAt ?? new Date().toISOString(),
  };
  await storage.save(scopeKey(scope), stamped);
}

/** Clear preference at scope (fall back to parent scope). */
export async function clearFacePreference(
  storage: FacePreferenceStorage,
  scope: FaceScope,
): Promise<void> {
  await storage.remove(scopeKey(scope));
}
